/**
 * Formato de fechas del perfil: «Cliente desde {mes} de {año}» de la cabecera (§7.2 de B10), las fechas civiles
 * `Y-m-d` de «Mis pedidos» (`formatIsoDate`) y el día de un instante ISO 8601 de la lista de deseos
 * (`formatIsoDateTimeAsDate`, `added_at`).
 *
 * Misma regla que `events/utils/event-format.util.ts`: el mes se calcula **en hora de Costa Rica**, nunca en
 * la del navegador ni en la del proceso SSR (UTC en el hosting). Una cuenta creada el 30 de septiembre a las
 * 21:00 de CR es `2026-10-01T03:00:00Z`: sin `timeZone` saldría «octubre». `tools/check-blog-timezone.mjs`
 * lo comprueba con el proceso en UTC.
 */

import { STORE_TIMEZONE } from '../../events/utils/event-format.util';
import { PROFILE_COPY } from '../profile.copy';

// Se construye en el primer uso, no al cargar el módulo (mismo motivo que en eventos).
let monthYear: Intl.DateTimeFormat | null = null;
let dayMonthYear: Intl.DateTimeFormat | null = null;

/** @internal Solo para tests que sustituyen `Intl.DateTimeFormat` (ver `resetEventFormatterCache`). */
export function resetProfileFormatterCache(): void {
  monthYear = null;
  dayMonthYear = null;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Fecha civil `Y-m-d` de la API (ya en hora de Costa Rica) → `dd/mm/aaaa`. Recompone la cadena SIN `Date`: un
 * `new Date('2026-09-30')` es medianoche UTC, y en Costa Rica (UTC−6) `getDate()` daría el 29. Lo que no case
 * `^\d{4}-\d{2}-\d{2}$` (incluido `null`) → `''`.
 */
export function formatIsoDate(value: string | null | undefined): string {
  const match = value ? ISO_DATE.exec(value) : null;
  if (!match) return '';
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}

/**
 * `created_at` de `/identify-user` (Laravel 9 manda microsegundos: `2026-09-30T17:10:00.000000Z`, que `Date`
 * lee en V8) → «Cliente desde septiembre de 2026». `null` si falta o no se puede leer: la línea no se pinta.
 */
export function formatClientSince(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  monthYear ??= new Intl.DateTimeFormat('es-CR', { month: 'long', year: 'numeric', timeZone: STORE_TIMEZONE });
  const parts = monthYear.formatToParts(date);
  const month = parts.find((p) => p.type === 'month')?.value;
  const year = parts.find((p) => p.type === 'year')?.value;
  if (!month || !year) return null;

  return PROFILE_COPY.HEADER.CLIENT_SINCE.replace('{mes}', month.toLocaleLowerCase('es-CR')).replace('{año}', year);
}

/**
 * Instante ISO 8601 de la API (`added_at` de la lista de deseos: `2026-10-01T03:00:00.000000Z`) → `dd/mm/aaaa` del día
 * **en Costa Rica**: ese instante es el 30/09/2026 a las 21:00 de CR. Lo que no se pueda leer (incluido `null`) → `''`.
 */
export function formatIsoDateTimeAsDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  dayMonthYear ??= new Intl.DateTimeFormat('es-CR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: STORE_TIMEZONE,
  });
  const parts = dayMonthYear.formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value;
  const [day, month, year] = [part('day'), part('month'), part('year')];
  return day && month && year ? `${day}/${month}/${year}` : '';
}
